import React, { useState, useEffect } from 'react';
import { FaUsers, FaCalendarAlt, FaTicketAlt, FaStar, FaHeart, FaQrcode } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import StatCard from '../components/dashboard/StatCard';
import '../styles/DashboardPage.css';

const DashboardPage = () => {
  const { business } = useAuth();
  const { activeLocation, locations } = useLocation();

  const [stats, setStats]         = useState({ followers: 0, bookings: 0, scans: 0, couponsUsed: 0, rating: 0 });
  const [followers, setFollowers] = useState([]);
  const [recentScans, setRecentScans]   = useState([]);   // point_logs
  const [recentCoupons, setRecentCoupons] = useState([]); // coupon_logs
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('activity'); // 'activity' | 'coupons'

  useEffect(() => {
    if (business?.id) loadAll();
  }, [business, activeLocation]);

  const loadAll = async () => {
    setLoading(true);
    await Promise.all([loadStats(), loadFollowers(), loadScans(), loadCouponLogs()]);
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      // Pending rezervasiyalar
      const { data: bookingsData } = await supabase
        .from('flash_bookings')
        .select('id', { count: 'exact' })
        .eq('business_id', business.id)
        .eq('status', 'pending');

      // Ümumi scan sayı
      const { data: scanData } = await supabase
        .from('point_logs')
        .select('id', { count: 'exact' })
        .eq('business_id', business.id);

      // Ümumi kupon sayı (bu biznesdə oxudulan)
      const { data: couponData } = await supabase
        .from('coupon_logs')
        .select('id', { count: 'exact' })
        .eq('business_id', business.id);

      // Orta reytinq
      const { data: ratingData } = await supabase
        .from('reviews')
        .select('rating')
        .eq('business_id', business.id);
      const avg = ratingData?.length
        ? (ratingData.reduce((s, r) => s + r.rating, 0) / ratingData.length).toFixed(1)
        : '—';

      setStats(prev => ({
        ...prev,
        bookings:    bookingsData?.length || 0,
        scans:       scanData?.length || 0,
        couponsUsed: couponData?.length || 0,
        rating:      avg,
      }));
    } catch (e) { console.error('loadStats:', e); }
  };

  const loadFollowers = async () => {
    if (!business?.id) return;
    let q = supabase
      .from('business_follows')
      .select('created_at, user_id, location_id')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false })
      .limit(20);
    if (activeLocation?.id) q = q.eq('location_id', activeLocation.id);

    let { data, error } = await q;
    if (error) {
      ({ data, error } = await supabase
        .from('business_follows')
        .select('created_at, user_id')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false })
        .limit(20));
    }
    if (!data?.length) { setFollowers([]); return; }

    const userIds = data.map(f => f.user_id);
    const { data: usersData } = await supabase
      .from('users').select('id, full_name, username, avatar_url').in('id', userIds);
    const usersMap = Object.fromEntries((usersData || []).map(u => [u.id, u]));
    const locsMap  = Object.fromEntries((locations || []).map(l => [l.id, l]));

    setFollowers(data.map(f => ({
      ...f,
      users:    usersMap[f.user_id] || null,
      location: f.location_id ? (locsMap[f.location_id] || null) : null,
    })));
    setStats(prev => ({ ...prev, followers: data.length }));
  };

  const loadScans = async () => {
    const { data } = await supabase
      .from('point_logs')
      .select('*')
      .eq('business_id', business.id)
      .order('created_at', { ascending: false })
      .limit(15);
    setRecentScans(data || []);
  };

  const loadCouponLogs = async () => {
    const { data, error } = await supabase
      .rpc('get_coupon_logs', { p_business_id: business.id });
    if (error) console.error('coupon_logs error:', error.message);
    setRecentCoupons(data || []);
  };

  const timeAgo = (ts) => {
    const diff = Date.now() - new Date(ts);
    const m = Math.floor(diff / 60000);
    const h = Math.floor(diff / 3600000);
    const d = Math.floor(diff / 86400000);
    if (m < 1)  return 'İndicə';
    if (m < 60) return `${m} dəq əvvəl`;
    if (h < 24) return `${h} saat əvvəl`;
    return `${d} gün əvvəl`;
  };

  const TIER_COLOR = { '5-20': '#3B82F6', '20-50': '#F59E0B', '50+': '#10B981', 'signup_bonus': '#8B5CF6' };
  const TIER_LABEL = { '5-20': '5–20 ₼ (+10 xal)', '20-50': '20–50 ₼ (+35 xal)', '50+': '50+ ₼ (+80 xal)', 'signup_bonus': '🎁 Qeydiyyat Bonusu' };

  if (loading) return <div className="dashboard-loading"><h2>Yüklənir...</h2></div>;

  return (
    <div className="dashboard-page">
      <div className="dashboard-header">
        <h1>Xoş Gəldiniz, {business?.name}! 👋</h1>
        <p>Biznesinizin canlı statistikası</p>
      </div>

      {/* ── STAT CARDS ── */}
      <div className="stats-grid">
        <StatCard icon={<FaHeart />}      title="İzləyicilər"   value={stats.followers}    color="#FF6B6B" />
        <StatCard icon={<FaCalendarAlt />} title="Gözləyən Rez." value={stats.bookings}     color="#f093fb" />
        <StatCard icon={<FaQrcode />}     title="Ümumi Scan"    value={stats.scans}         color="#667eea" />
        <StatCard icon={<FaTicketAlt />}  title="İstif. Kupon"  value={stats.couponsUsed}   color="#E63950" />
        <StatCard icon={<FaStar />}       title="Reytinq"       value={stats.rating}        color="#feca57" />
      </div>

      {/* ── FOLLOWERS ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', marginBottom: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <FaHeart style={{ color: '#FF6B6B' }} />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>İzləyicilər</h3>
          <span style={{ marginLeft: 4, background: '#FFF0F0', color: '#FF6B6B', borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700 }}>
            {followers.length}
          </span>
        </div>
        {followers.length === 0 ? (
          <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>Hələ heç kim sizi izləməyib.</p>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {followers.map((f, i) => {
              const u = f.users;
              const name = u?.full_name || u?.username || 'İstifadəçi';
              const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
              const branchName = !activeLocation && locations.length > 1 ? f.location?.name : null;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#F7F8FF', borderRadius: 30, padding: '6px 12px 6px 6px' }}>
                  {u?.avatar_url ? (
                    <img src={av(u.avatar_url)} alt={name} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, fontWeight: 700 }}>
                      {initials}
                    </div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{name}</span>
                    {branchName && <span style={{ fontSize: 11, color: '#667eea', fontWeight: 500 }}>{branchName}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── ACTIVITY TABS ── */}
      <div style={{ background: '#fff', borderRadius: 16, padding: '20px 24px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        {/* Tab header */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <button
            onClick={() => setTab('activity')}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
              background: tab === 'activity' ? '#1a1a2e' : '#F1F5F9',
              color: tab === 'activity' ? '#fff' : '#64748B',
              fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>
            👤 Scan Tarixçəsi ({recentScans.length})
          </button>
          <button
            onClick={() => setTab('coupons')}
            style={{
              flex: 1, padding: '10px 0', borderRadius: 10, border: 'none',
              background: tab === 'coupons' ? '#E63950' : '#F1F5F9',
              color: tab === 'coupons' ? '#fff' : '#64748B',
              fontWeight: 700, fontSize: 14, cursor: 'pointer',
            }}>
            🎟️ Kupon Tarixçəsi ({recentCoupons.length})
          </button>
        </div>

        {/* Scan logs */}
        {tab === 'activity' && (
          recentScans.length === 0 ? (
            <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Hələ scan edilməyib</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentScans.map(log => {
                const color = TIER_COLOR[log.amount_tier] || '#888';
                const tierLabel = TIER_LABEL[log.amount_tier] || log.amount_tier;
                return (
                  <div key={log.id} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', background: '#F8F9FF', borderRadius: 12 }}>
                    <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>👤</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{log.user_name}</div>
                      <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2 }}>{timeAgo(log.created_at)}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color, background: color + '18', padding: '3px 10px', borderRadius: 20, marginBottom: 4 }}>{tierLabel}</div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: '#F0941F' }}>+{log.points} xal</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )
        )}

        {/* Coupon logs */}
        {tab === 'coupons' && (
          recentCoupons.length === 0 ? (
            <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: '20px 0' }}>Hələ kupon oxudulmayıb</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {recentCoupons.map(log => (
                <div key={log.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '14px 16px', background: '#FFF5F5', borderRadius: 12, border: '1px solid #FFE4E4' }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🎟️</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{log.coupon_title}</div>
                    {log.coupon_description && (
                      <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{log.coupon_description}</div>
                    )}
                    <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
                      👤 {log.user_name} · {timeAgo(log.created_at)}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#10B981', background: '#ECFDF5', padding: '4px 10px', borderRadius: 20, flexShrink: 0 }}>
                    ✓ İstifadə edildi
                  </div>
                </div>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
